import React, { useMemo, useState } from "react";
import { Card, Form, Row, Col, Badge, Alert, Button } from "react-bootstrap";
import {
  Clock,
  Calendar,
  List,
  Edit3,
  XCircle,
  Tag,
  Heart,
  X,
} from "react-feather";

/**
 * AI BOT Settings Editor
 *
 * Stores configuration inside booking engine config JSON under: config.aiBot
 *
 * Requirements:
 * - Toggle AI BOT On/Off
 * - If Off: nothing should be shown on website (widget will honor config.aiBot.enabled)
 * - If On: ask assistant names (comma-separated), chat bubble name, bot options toggle + selectable tiles,
 *          and training data (<= 300 words)
 * - Bot options: fixed list; user selects via dropdown one-by-one, sees selected list, can remove
 */

const DEFAULT_AIBOT = {
  enabled: false,
  assistantNames: "", // comma-separated (e.g., "Jiya, Meacy")
  chatBubbleName: "", // e.g., "Talk with Front Office"
  showQuickActions: true,
  quickActions: {
    availability: true,
    book: true,
    myBookings: true,
    modify: true,
    cancel: true,
    offers: true,
    eventsWedding: true, // NEW
  },
  trainingData: "", // max 300 words
};

const FIXED_ACTIONS = [
  { key: "availability", label: "Check availability", Icon: Clock },
  { key: "book", label: "Make booking", Icon: Calendar },
  { key: "myBookings", label: "My bookings", Icon: List },
  { key: "modify", label: "Modify booking", Icon: Edit3 },
  { key: "cancel", label: "Cancel booking", Icon: XCircle },
  { key: "offers", label: "Offers", Icon: Tag },
  { key: "eventsWedding", label: "Events & Wedding", Icon: Heart },
];

const getWords = (text) => {
  const t = String(text || "").trim();
  if (!t) return [];
  return t.split(/\s+/).filter(Boolean);
};

const trimToWords = (text, maxWords) => {
  const words = getWords(text);
  if (words.length <= maxWords)
    return { value: String(text || ""), wordCount: words.length, trimmed: false };
  return {
    value: words.slice(0, maxWords).join(" "),
    wordCount: maxWords,
    trimmed: true,
  };
};

export default function AiBotSettingsEditor({ branding, setBranding }) {
  const aiBot = useMemo(() => {
    const raw = branding?.aiBot || {};
    return {
      ...DEFAULT_AIBOT,
      ...raw,
      quickActions: {
        ...DEFAULT_AIBOT.quickActions,
        ...(raw.quickActions || {}),
      },
    };
  }, [branding]);

  const [trimNotice, setTrimNotice] = useState(false);
  const [addKey, setAddKey] = useState("");

  const updateAiBot = (patch) => {
    setBranding((prev) => {
      const prevBot = prev?.aiBot || {};
      return {
        ...prev,
        aiBot: {
          ...DEFAULT_AIBOT,
          ...prevBot,
          ...patch,
          quickActions: {
            ...DEFAULT_AIBOT.quickActions,
            ...(prevBot.quickActions || {}),
            ...(patch.quickActions || {}),
          },
        },
      };
    });
  };

  const selectedKeys = useMemo(() => {
    const qa = aiBot.quickActions || {};
    return FIXED_ACTIONS.map((a) => a.key).filter((k) => !!qa[k]);
  }, [aiBot.quickActions]);

  const availableActions = useMemo(() => {
    const qa = aiBot.quickActions || {};
    return FIXED_ACTIONS.filter((a) => !qa[a.key]);
  }, [aiBot.quickActions]);

  const wordCount = useMemo(() => getWords(aiBot.trainingData).length, [aiBot.trainingData]);
  const namesCount = useMemo(() => {
    return String(aiBot.assistantNames || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean).length;
  }, [aiBot.assistantNames]);

  const onTrainingChange = (next) => {
    const { value, trimmed } = trimToWords(next, 300);
    setTrimNotice(trimmed);
    updateAiBot({ trainingData: value });
  };

  const addAction = (key) => {
    if (!key) return;
    updateAiBot({ quickActions: { [key]: true } });
  };

  const removeAction = (key) => {
    updateAiBot({ quickActions: { [key]: false } });
  };

  return (
    <Card className="p-4 border-0 shadow-sm">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="fw-bold mb-1">AI BOT</h5>
          <div className="text-muted" style={{ fontSize: 13 }}>
            Enable/disable the AI assistant and configure launcher text, quick-actions, and training notes.
          </div>
        </div>
        <Form.Check
          type="switch"
          id="aiBotEnabled"
          label={aiBot.enabled ? "On" : "Off"}
          checked={!!aiBot.enabled}
          onChange={(e) => updateAiBot({ enabled: e.target.checked })}
        />
      </div>

      {!aiBot.enabled ? (
        <Alert variant="secondary" className="mb-0">
          AI BOT is currently disabled. Nothing will be shown on the booking engine.
        </Alert>
      ) : (
        <>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  AI Assistant Name <span className="text-muted">(comma separated)</span>
                </Form.Label>
                <Form.Control
                  value={aiBot.assistantNames}
                  onChange={(e) => updateAiBot({ assistantNames: e.target.value })}
                  placeholder="Jiya, Meacy"
                />
                <Form.Text className="text-muted">
                  Example: <b>Jiya, Meacy</b>.
                  {namesCount > 0 ? (
                    <span className="ms-2">
                      <Badge bg="info">{namesCount} name(s)</Badge>
                    </span>
                  ) : null}
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>ChatBubble Name</Form.Label>
                <Form.Control
                  value={aiBot.chatBubbleName}
                  onChange={(e) => updateAiBot({ chatBubbleName: e.target.value })}
                  placeholder="Talk with Front Office"
                />
                <Form.Text className="text-muted">
                  This label is shown on the chat launcher bubble/button.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Card
            className="p-3 mb-3"
            style={{ background: "#fafafa", border: "1px solid rgba(0,0,0,0.06)" }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="fw-semibold">Bot Options (Quick Actions)</div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Show quick action tiles (fixed list) when the chat starts.
                </div>
              </div>
              <Form.Check
                type="switch"
                id="aiBotShowQuickActions"
                label={aiBot.showQuickActions ? "Shown" : "Hidden"}
                checked={!!aiBot.showQuickActions}
                onChange={(e) => updateAiBot({ showQuickActions: e.target.checked })}
              />
            </div>

            {aiBot.showQuickActions && (
              <>
                <Row className="mt-3 align-items-end">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="mb-1">Add an option</Form.Label>
                      <Form.Select
                        value={addKey}
                        onChange={(e) => {
                          const k = e.target.value;
                          setAddKey("");
                          addAction(k);
                        }}
                      >
                        <option value="">Select option…</option>
                        {availableActions.map((a) => (
                          <option key={a.key} value={a.key}>
                            {a.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Select one-by-one. Selected items appear below and can be removed.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4} className="text-md-end mt-2 mt-md-0">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={selectedKeys.length === 0}
                      onClick={() => {
                        // Clear all
                        const next = {};
                        FIXED_ACTIONS.forEach((a) => {
                          next[a.key] = false;
                        });
                        updateAiBot({ quickActions: next });
                      }}
                    >
                      Clear all
                    </Button>
                  </Col>
                </Row>

                {/* Selected list */}
                <div className="mt-3 d-flex flex-wrap" style={{ gap: 8 }}>
                  {selectedKeys.length === 0 ? (
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      No options selected.
                    </div>
                  ) : (
                    selectedKeys.map((k) => {
                      const a = FIXED_ACTIONS.find((x) => x.key === k);
                      if (!a) return null;
                      const Icon = a.Icon;
                      return (
                        <span
                          key={k}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 10px",
                            borderRadius: 999,
                            border: "1px solid rgba(15,23,42,0.12)",
                            background: "#fff",
                            boxShadow: "0 10px 18px rgba(15,23,42,0.06)",
                          }}
                        >
                          <span
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: 9,
                              display: "grid",
                              placeItems: "center",
                              background: "rgba(15,23,42,0.06)",
                            }}
                          >
                            <Icon size={14} />
                          </span>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{a.label}</span>
                          <button
                            type="button"
                            onClick={() => removeAction(k)}
                            aria-label={`Remove ${a.label}`}
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              marginLeft: 2,
                              cursor: "pointer",
                              color: "rgba(15,23,42,0.65)",
                              display: "grid",
                              placeItems: "center",
                            }}
                          >
                            <X size={14} />
                          </button>
                        </span>
                      );
                    })
                  )}
                </div>

                {/* Preview tiles (non-interactive) */}
                {selectedKeys.length > 0 && (
                  <div
                    className="mt-3"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: 12,
                    }}
                  >
                    {FIXED_ACTIONS.filter((a) => selectedKeys.includes(a.key)).map(
                      ({ key, label, Icon }) => (
                        <div
                          key={key}
                          style={{
                            border: "1px solid rgba(15,23,42,0.10)",
                            borderRadius: 12,
                            padding: 12,
                            background: "#fff",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            boxShadow: "0 10px 26px rgba(15,23,42,0.08)",
                          }}
                        >
                          <div
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 10,
                              display: "grid",
                              placeItems: "center",
                              background: "rgba(15,23,42,0.06)",
                            }}
                          >
                            <Icon size={18} />
                          </div>
                          <div style={{ lineHeight: 1.15 }}>
                            <div style={{ fontWeight: 800 }}>{label}</div>
                            <div style={{ fontSize: 12, color: "rgba(15,23,42,0.6)" }}>
                              Tap to begin
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
              </>
            )}
          </Card>

          <Form.Group className="mb-2">
            <div className="d-flex align-items-center justify-content-between">
              <Form.Label className="mb-1">BOT Training Data</Form.Label>
              <Badge bg={wordCount > 300 ? "danger" : wordCount > 260 ? "warning" : "secondary"}>
                {wordCount}/300 words
              </Badge>
            </div>
            <Form.Control
              as="textarea"
              rows={6}
              value={aiBot.trainingData}
              onChange={(e) => onTrainingChange(e.target.value)}
              placeholder="Add FAQ-style notes to help the bot answer basic enquiries (check-in time, breakfast, parking, policies, etc.). Max 300 words."
            />
            <Form.Text className="text-muted">
              Keep it concise and factual.
              {trimNotice ? (
                <span className="ms-2 text-danger">Max 300 words — extra text was trimmed.</span>
              ) : null}
            </Form.Text>
          </Form.Group>
        </>
      )}
    </Card>
  );
}
